import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { axiosInstance } from '../../services/api';
import { getUser } from '../../utils/secureStorage';

function SetupComplete() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [familyData, setFamilyData] = useState(null);
  const [userData, setUserData] = useState(null);
  const [invitations, setInvitations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Check if user joined via invitation
  const isJoinedMember = searchParams.get('joined') === 'true';

  useEffect(() => {
    // Load data from localStorage and/or API
    const loadData = async () => {
      try {
        // Get user data
        const user = getUser();
        setUserData(user);

        if (isJoinedMember) {
          // For joined members, fetch their family info from API
          try {
            const response = await axiosInstance.get('/humans/family-members/my-family/');
            if (response.data) {
              setFamilyData({
                familyName: response.data.family?.name || response.data.name || 'Your Family',
                role: response.data.role || 'member',
                isJoined: true
              });
            }
          } catch (error) {
            console.error('Error fetching family data for joined member:', error);
            // Set minimal data for joined members
            setFamilyData({
              familyName: null, // Will show "Welcome!" without family name
              role: 'member',
              isJoined: true
            });
          }
        } else {
          // For family creators, use localStorage data
          const familySetupStr = sessionStorage.getItem('familySetupData');
          const familySetup = familySetupStr ? JSON.parse(familySetupStr) : null;
          setFamilyData(familySetup);

          // Try to fetch invitations from API (only for family creators)
          const familyId = localStorage.getItem('familyId');
          if (familyId) {
            try {
              const response = await axiosInstance.get(`/account/invitations/?family=${familyId}`);
              setInvitations(response.data.results || response.data || []);
            } catch (error) {
              console.error('Error fetching invitations:', error);
              // Continue without invitations
            }
          }
        }
      } catch (error) {
        console.error('Error loading setup data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [isJoinedMember]);

  const handleStartSharing = () => {
    // Navigate to My Life page to start chapters onboarding
    navigate('/my-life');
  };

  const handleInviteMore = () => {
    navigate('/auth/invite-family');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#F5F5DC] flex items-center justify-center">
        <div className="text-center">
          <div className="text-2xl text-[#556B2F]">Loading...</div>
        </div>
      </div>
    );
  }

  const familyName = familyData?.familyName;
  const userName = userData ? `${userData.first_name} ${userData.last_name}` : 'You';
  const userRole = isJoinedMember ? 'Member' : (familyData?.role || 'Family Creator');
  const memberCount = 1 + invitations.length;

  // Different titles for joined members vs family creators
  const headerTitle = isJoinedMember ? "Welcome!" : "Welcome to Your Family!";
  const headerSubtitle = isJoinedMember
    ? "You've successfully joined your family"
    : "Your story collection starts now";

  return (
    <div className="min-h-screen bg-[#F5F5DC] flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-300">
          {/* Header */}
          <div className="bg-[#556B2F] text-white p-6 text-center">
            <h1 className="text-2xl font-bold mb-2">{headerTitle}</h1>
            <p className="text-base opacity-90">{headerSubtitle}</p>
          </div>

          {/* Content */}
          <div className="p-8">
            {/* Success Icon */}
            <div className="text-center text-5xl mb-5">🎉</div>

            {/* Family Info - only show if we have a family name */}
            {familyName && (
              <div className="text-center mb-8">
                <h3 className="text-lg font-bold text-[#556B2F] mb-3">{familyName}</h3>
                {!isJoinedMember && (
                  <p className="text-gray-600">{memberCount} member{memberCount !== 1 ? 's' : ''} • 0 stories shared</p>
                )}
              </div>
            )}

            {/* Family Members */}
            <div className="space-y-3 mb-8">
              {/* Current User */}
              <div className="flex items-center p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div className="w-12 h-12 rounded-full bg-[#556B2F] flex items-center justify-center text-white font-bold text-xl mr-4">
                  {userData?.first_name?.[0] || 'U'}
                </div>
                <div>
                  <h3 className="font-bold text-gray-900">{userName} (You)</h3>
                  <p className="text-sm text-gray-600 capitalize">{userRole}</p>
                </div>
              </div>

              {/* Invited Members - only show for family creators */}
              {!isJoinedMember && invitations.map((invitation, index) => (
                <div key={index} className="flex items-center p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="w-12 h-12 rounded-full bg-gray-300 flex items-center justify-center text-gray-600 font-bold text-xl mr-4">
                    ?
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900">Invitation Sent</h3>
                    <p className="text-sm text-gray-600">
                      {invitation.invited_email || 'Pending'} • {invitation.relationship_name || 'Family Member'}
                    </p>
                  </div>
                </div>
              ))}

              {/* Show invite message only for family creators with no invitations */}
              {!isJoinedMember && invitations.length === 0 && (
                <div className="flex items-center p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="text-sm text-blue-800">
                    💡 Invite family members to start building your family tree together!
                  </div>
                </div>
              )}

              {/* Show welcome message for joined members */}
              {isJoinedMember && (
                <div className="flex items-center p-4 bg-green-50 rounded-lg border border-green-200">
                  <div className="text-sm text-green-800">
                    ✅ You can now view the family tree and share your stories with family!
                  </div>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
              <button
                onClick={handleStartSharing}
                className="w-full bg-[#556B2F] text-white border-none py-4 px-4 text-lg font-bold rounded-lg cursor-pointer transition-colors hover:bg-[#4A5D23]"
              >
                Start Sharing Stories
              </button>

              <button
                onClick={isJoinedMember ? () => navigate('/family-hub') : handleInviteMore}
                className="w-full bg-[#6B8E23] text-white border-none py-4 px-4 text-base rounded-lg cursor-pointer transition-colors hover:bg-[#5A7A1F]"
              >
                {isJoinedMember ? 'View Family Tree' : 'Invite More Family'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SetupComplete; 